import React, { useEffect, useState } from 'react';

import {
  CTA,
  InputAppearanceValue,
  ThemeProvider,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import { formatPhoneNumber } from '../../../../shared/utils/general';
import { ManageContactsMutation } from '../../../../shared/typings/main';
import {
  StyledManageContactsPhoneFormWrapper,
  StyledManageContactsPhoneButtonContainer,
} from './styled';
import { onVWBasicButtonClickWithProcessEvent } from '../../../../shared/tagging/tagging-helpers';

const US_MOBILE_NUMBER_REGEX = /^[0-9]{10}$/;

const buildPhoneSchema = (existingNumbers: string[]) =>
  Yup.object({
    number: Yup.string()
      .matches(US_MOBILE_NUMBER_REGEX, {
        message: 'accountSettings.manage.contacts.interaction.layer.sms.alerts.add.edit.error.invalid',
        excludeEmptyString: true,
      })
      .notOneOf(existingNumbers, 'accountSettings.manage.contacts.interaction.layer.sms.alerts.add.edit.error.duplicate'),
  });


interface ManageContactsAddEditPhoneViewProps {
  trackingManager: any;
  flow: 'add' | 'edit';
  onSave: (mutation: ManageContactsMutation) => Promise<void>;
  onSuccess: () => void;
  onError: () => void;
  onChange: () => void;
  onResetChanges: () => void;
  translationHandler: (key: string) => string;
  initialPhoneNumber?: string;
  existingNumbers?: string[];
}

export const ManageContactsAddEditPhoneView: React.FC<ManageContactsAddEditPhoneViewProps> = ({
  trackingManager,
  flow,
  onSave,
  onSuccess,
  onError,
  onChange,
  onResetChanges,
  translationHandler,
  initialPhoneNumber = '',
  existingNumbers = [],
}) => {
  const [formattedNumber, setFormattedNumber] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const initialFormatted = formatPhoneNumber(initialPhoneNumber).formatted;

  const savePhoneNumber = async (number: string) => {
    setIsSubmitting(true);
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: `Personal Information and Security - Manage Contacts - ${flow === 'add' ? 'Add Mobile Number' : 'Edit Mobile Number'}`,
        StepNumber: '4.5',
      }, {
        name: flow === 'add' ? 'Add mobile number' : 'Edit mobile number',
      });

      await onSave({ isEdit: flow === 'edit', isRemove: false, initialValue: initialPhoneNumber.replace(/\D/g, ''), value: number });
      onSuccess();
    } catch (error) {
      console.error('There was an error saving the phone number', error);
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: { number: initialPhoneNumber },
    onSubmit: async ({ number }) => {
      await savePhoneNumber(number);
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: buildPhoneSchema(
      existingNumbers.filter(n => n !== initialPhoneNumber)
    ),
  });

  const handlePhoneChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { formatted, raw } = formatPhoneNumber(e.currentTarget.value);
    setFormattedNumber(formatted);
    formik.setFieldValue('number', raw);
  };

  const isButtonDisabled =
    Object.keys(formik.errors).length > 0 ||
    !formik.dirty ||
    !formik.values.number ||
    formik.isSubmitting ||
    isSubmitting;

  useEffect(() => {
    setFormattedNumber(initialFormatted);
  }, [initialFormatted]);

  useEffect(() => {
    if (formik.dirty) onChange();
    else onResetChanges();
  }, [formik.values, onChange, onResetChanges, formik.dirty]);

  return (
    <ThemeProvider theme="main">
      <StyledManageContactsPhoneFormWrapper>
        <form onSubmit={formik.handleSubmit}>
          <NoTranslateTextInput
            value={formattedNumber}
            onChange={handlePhoneChange}
            onBlur={formik.handleBlur('number')}
            name="number"
            message={
              formik.touched.number && formik.errors.number
                ? translationHandler(formik.errors.number)
                : undefined
            }
            appearance={
              formik.touched.number && formik.errors.number
                ? ('error' as InputAppearanceValue)
                : undefined
            }
            aria-label={translationHandler('accountSettings.manage.contacts.interaction.layer.sms.alerts.add.edit.phone.text.input.label')}
            aria-required="true"
            label={translationHandler('accountSettings.manage.contacts.interaction.layer.sms.alerts.add.edit.phone.text.input.label')}
          />
          <StyledManageContactsPhoneButtonContainer>
            <CTA
              aria-label={flow === 'add' ? 'Add phone number CTA' : 'Save phone number CTA'}
              disabled={isButtonDisabled}
              emphasis="primary"
              stretchContent
              tag="button"
              type="submit"
            >
              {translationHandler(flow === 'add'
                ? 'accountSettings.manage.contacts.interaction.layer.sms.alerts.add.phone.cta'
                : 'accountSettings.manage.contacts.interaction.layer.sms.alerts.edit.phone.cta'
              )}
            </CTA>
          </StyledManageContactsPhoneButtonContainer>
        </form>
      </StyledManageContactsPhoneFormWrapper>
    </ThemeProvider>
  );
};
